import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import api from "../../apis/EdisonApi";
import { toast } from "react-toastify";

const formatTimestamp = (timestamp) => {
	if (!timestamp) return "Not available";

	const date = new Date(timestamp * 1000);
	return Number.isNaN(date.getTime()) ? "Not available" : date.toLocaleString();
};

const formatMetric = (value) =>
	value === undefined || value === null || value === "" ? "—" : value;

const Shelly = () => {
	const [status, setStatus] = useState(null);
	const [isToggling, setIsToggling] = useState(false);

	const getStatus = useCallback(async (signal) => {
		try {
			const response = await api.get("/shelly/status", { signal });
			setStatus({ ...response.data.data, timestamp: response.data.timestamp });
		} catch (error) {
			if (error.name !== "CanceledError") {
				toast.error("Unable to retrieve the Shelly status");
			}
		}
	}, []);

	useEffect(() => {
		const controller = new AbortController();
		getStatus(controller.signal);
		return () => controller.abort();
	}, [getStatus]);

	const device = status?.data;
	const relay = device?.device_status?.relays?.[0];
	const meter = device?.device_status?.meters?.[0];
	const update = device?.device_status?.update;
	const isOnline = device?.online === true;
	const isOn = relay?.ison === true;
	const lastUpdate = formatTimestamp(status?.timestamp);

	const toggleState = async () => {
		if (!relay || !isOnline || isToggling) return;

		try {
			setIsToggling(true);
			const response = await api.post("/shelly/status", { set: isOn ? "off" : "on" });

			if (response.data.isok !== true) {
				toast.warning("The smart plug did not accept the command");
				return;
			}

			await getStatus();
			toast.success(`Smart plug turned ${isOn ? "off" : "on"}`);
		} catch {
			toast.error("Unable to update the Shelly smart plug");
		} finally {
			setIsToggling(false);
		}
	};

	const details = [
		["Device ID", "shelly1"],
		["Connectivity", status ? (isOnline ? "Connected" : "Disconnected") : "Loading…"],
		["Relay", status ? (isOn ? "On" : "Off") : "Loading…"],
		["Firmware", status ? (update?.has_update ? "Update available" : "Up to date") : "Loading…"],
		["Last update", status ? lastUpdate : "Loading…"],
	];

	return (
		<div className="right_col device-page shelly-page" role="main">
			<header className="page-heading device-page-heading">
				<div>
					<p className="eyebrow">CONNECTED DEVICE</p>
					<h1>Shelly Smart Plug</h1>
					<p>Live status, energy measurements and remote control.</p>
				</div>
				<span className={`device-status-badge${isOnline ? " device-status-badge--online" : ""}`}>
					<span aria-hidden="true" />
					{status ? (isOnline ? "Connected" : "Disconnected") : "Loading"}
				</span>
			</header>

			<section className="shelly-overview" aria-label="Shelly smart plug overview">
				<article className="shelly-control-card">
					<div className="shelly-control-card__heading">
						<span>Smart plug</span>
						<small>{isOnline ? "Available for remote control" : "Remote control unavailable"}</small>
					</div>

					<div className="shelly-control-card__state" aria-live="polite">
						<span className={`shelly-control-card__icon${isOn ? " shelly-control-card__icon--on" : ""}`} aria-hidden="true">
							<FontAwesomeIcon icon={faLightbulb} />
						</span>
						<div>
							<small>Current state</small>
							<strong>{status ? (isOn ? "On" : "Off") : "—"}</strong>
						</div>
					</div>

					<button
						className="btn btn-primary device-action-button"
						type="button"
						onClick={toggleState}
						disabled={!status || !isOnline || isToggling}
					>
						{isToggling ? "Applying…" : `Turn ${isOn ? "off" : "on"}`}
					</button>
				</article>

				<div className="shelly-metrics-grid">
					<article className="device-metric-card">
						<span>Power</span>
						<strong>{formatMetric(meter?.power)} <small>W</small></strong>
						<p>Current active power</p>
					</article>
					<article className="device-metric-card">
						<span>Overpower</span>
						<strong>{formatMetric(meter?.overpower)} <small>W</small></strong>
						<p>Reported overload value</p>
					</article>
					<article className="device-metric-card">
						<span>Total energy</span>
						<strong>{formatMetric(meter?.total)} <small>Wh</small></strong>
						<p>Accumulated consumption</p>
					</article>
					<article className="device-metric-card device-metric-card--time">
						<span>Last update</span>
						<strong>{status ? lastUpdate : "Loading…"}</strong>
						<p>Timestamp received by Domus IoT</p>
					</article>
				</div>
			</section>

			<section className="x_panel device-details-panel" aria-labelledby="shelly-details-title">
				<div className="x_title">
					<h2 id="shelly-details-title">Device information</h2>
				</div>
				<div className="x_content">
					<dl className="device-details-grid">
						{details.map(([label, value]) => (
							<div key={label}>
								<dt>{label}</dt>
								<dd>{value}</dd>
							</div>
						))}
					</dl>
				</div>
			</section>
		</div>
	);
};

export default Shelly;
